import { Api } from "@penn/web-common";
import { USER_STORAGE_KEY } from "@penn/web-common";
import { authStorage } from "@penn/web-common";
import { unwrap } from "@penn/web-common";
import type { RegionCode } from "./filter";
import type { ApplicationDetail, FastenerBrandCode } from "./applications";
import { WORK_FOR_STORAGE_KEY } from "@penn/web-common";
import {
  createStableRequestKey,
  SharedRequestCache,
} from "@/utils/calculation-request-control";

const calculatePreviewRequestCache =
  new SharedRequestCache<CalculatePreviewResponse>({
    ttlMs: 5_000,
    maxEntries: 64,
  });
let calculatePreviewCacheGeneration = 0;

export function clearCalculatePreviewRequestCache(): void {
  calculatePreviewCacheGeneration += 1;
  calculatePreviewRequestCache.clear();
}

export function invalidateCalculatePreviewRequestCache(): void {
  // A new generation prevents callers after an assumptions/configuration
  // change from joining an older in-flight calculation. Existing consumers may
  // still finish safely, but their result cannot enter the new context's cache.
  calculatePreviewCacheGeneration += 1;
  calculatePreviewRequestCache.clearFulfilled();
}

export type MachineCostMode = "shared" | "dedicated";

export type CalculateFastenerInput = {
  brand: FastenerBrandCode;
  type?: string | null;
  qtyPerPart?: string;
  qtyPerPartA?: string;
  qtyPerPartB?: string;
};

export type CalculateApplicationInput = {
  applicationId?: string;
  name?: string | null;
  annualVolume?: string;
  batchSize?: string;
  fasteners: CalculateFastenerInput[];
};

export type ModelConstantOverrides = {
  autoFedTypes?: string;
};

export type CalculatePreviewRequest = {
  region: RegionCode;
  productId: string;
  machineCostMode: MachineCostMode;
  applications: CalculateApplicationInput[];
  modelConstantOverrides?: ModelConstantOverrides;
  /** POC configured solution investment (from configure modal / localStorage). */
  machinePriceOverride?: string;
  /**
   * Optimize-allocation cache key (Q-OPT-02). When present, svc-10 warms
   * `optimize_allocation_cache` as a side-effect of this preview (Q-OPT-04).
   */
  optimizeSessionId?: string;
};

export type ComparisonSummary = {
  costPerPart: string;
  costPerPartShared: string;
  costPerPartDedicated: string;
  machineCostPerPart: string;
  machineCostPerPartShared: string;
  machineCostPerPartDedicated: string;
  workerCostPerPart: string;
  qualityCostPerPart: string;
  machineUtilization?: string;
  workerUtilization?: string;
  requiredMachines: string;
  requiredOperators: string;
};

export type PerAppCostBreakdown = ComparisonSummary & {
  applicationId?: string;
  applicationName: string | null;
  workerCostPerBatch: string;
  qualityCostPerBatch: string;
  machineCostPerBatch: string;
  batchesPerYear: string;
};

export type CalculateUnavailableReason =
  | "no_cost_model"
  | "asia_pricing_unavailable"
  | "machine_price_unavailable";

export type CalculatePreviewResponse =
  | {
      ok: true;
      available: true;
      result: {
        perApp: PerAppCostBreakdown[];
        aggregate: {
          investmentCost: string;
          operatorCost: string;
          qualityCost: string;
          totalCost: string;
        };
        summary: ComparisonSummary;
        currencyCode: string;
      };
    }
  | {
      ok: true;
      available: false;
      reason: CalculateUnavailableReason;
      details?: Record<string, unknown>;
    };

export function applicationToCalculateInput(
  detail: ApplicationDetail,
): CalculateApplicationInput {
  return {
    applicationId: detail.applicationId,
    name: detail.name,
    annualVolume:
      detail.annualVolume != null ? String(detail.annualVolume) : undefined,
    batchSize: detail.batchSize != null ? String(detail.batchSize) : undefined,
    fasteners: detail.fasteners
      .filter((f) => f.brand)
      .map((f) => ({
        brand: f.brand as FastenerBrandCode,
        type: f.type,
        ...(f.qtyPerPart != null ? { qtyPerPart: String(f.qtyPerPart) } : {}),
        ...(f.qtyPerPartA != null ? { qtyPerPartA: String(f.qtyPerPartA) } : {}),
        ...(f.qtyPerPartB != null ? { qtyPerPartB: String(f.qtyPerPartB) } : {}),
      })),
  };
}

export function isApplicationValidForCalculate(detail: ApplicationDetail): boolean {
  if (!detail.annualVolume || detail.annualVolume <= 0) return false;
  if (!detail.batchSize || detail.batchSize <= 0) return false;
  if (!detail.fasteners.length) return false;
  return detail.fasteners.some((f) => {
    if (!f.brand) return false;
    const q = f.qtyPerPart ?? 0;
    const qa = f.qtyPerPartA ?? 0;
    const qb = f.qtyPerPartB ?? 0;
    return q > 0 || qa > 0 || qb > 0;
  });
}

export async function postCalculatePreview(
  body: CalculatePreviewRequest,
  signal?: AbortSignal,
): Promise<CalculatePreviewResponse> {
  // svc-10 resolves assumptions for the authenticated/effective Work-For user.
  // Keep the short-lived browser cache scoped to that identity so a rapid
  // account or Work-For switch cannot reuse another user's calculation.
  const key = createStableRequestKey("calculate-preview", {
    body,
    cacheGeneration: calculatePreviewCacheGeneration,
    user: authStorage.getItem(USER_STORAGE_KEY),
    workFor: authStorage.getItem(WORK_FOR_STORAGE_KEY),
  });
  return calculatePreviewRequestCache.getOrCreate(
    key,
    async (sharedSignal) => {
      const res = await Api.post("/v1/calculate", body, {
        signal: sharedSignal,
      });
      return unwrap<CalculatePreviewResponse>(res.data);
    },
    signal,
  );
}
